import React, { Dispatch, ReactNode, SetStateAction } from 'react';
import { useNoAuthHooks } from '../../../../../../../../Hooks/hooks';
import { formatPercent } from '../../../../../../../../Utilits/utilits';
import useMainCartDialog from './mainCartDialog-hook';
import { ICartItem, ICartOption, ItemCounterTypes } from '../../../../../../../../Interfaces/componentTypes';
import { VisitorPageContents } from '../../../../../visitorPage';
import { IMenuItemOptionResponce, IServices } from '../../../../../../../../Interfaces/responseTypes';
import ItemCounter from '../../ItemCounter/itemCounter';
import useLanguagesHooks from '../../../../../../../../Components/Languages/setLanguage-hooks';
import { DeliveryIconComponent, PlateIcon } from '../../../../../../../../Components/IconComponents';
import PackageIcon from '../../../../../../../../Components/IconComponents/packageIcon';
import { SmallTrash } from '../../../../../../../../Assets/Icons';
import { EmptyCart } from '../../../../../../../../Assets/Images';
import styles from './mainCartDialog.module.scss';

export interface IMainCartDialogProps {
    companyId: number,
    company_name: string,
    company_logo: string,
    company_contacts?: string[],
    start_time?: string | null,
    end_time?: string | null,
    cart: [] | ICartItem[];
    setMainCart: Dispatch<SetStateAction<[] | ICartItem[]>>,
    currencySymbol: string,
    serviceFee?: number,
    services: null | IServices,
    setDialog: Dispatch<SetStateAction<ReactNode | null>>;
    deliveryMinCost: number | null | undefined,
    onCloseDialog: () => void;
    setContentType: Dispatch<SetStateAction<VisitorPageContents>>
};

const MainCartDialog: React.FC<IMainCartDialogProps> = (props) => {
    const { currencySymbol, serviceFee, services, setMainCart } = props;
    const { baseURL, renderOptions } = useNoAuthHooks();
    const { t, activeLanguage } = useLanguagesHooks();
    const {
        dialogCart,
        dineInPrice,
        showEmptyCart,
        isAnyActiveService,
        setDialogCart,
        onOpenPreOrderDialog,
        removeFromDialogCart,
        onOpenOrderDeliveryDialog,
    } = useMainCartDialog(props);

    const handleOptionCountChange = (itemId: number, optionId: number, countChange: number) => {
        setDialogCart((prevCart) => {
            const updatedCart = prevCart.map((cartItem) => {
                if (cartItem.id === itemId) {
                    return {
                        ...cartItem,
                        option: cartItem.option.map((opt) =>
                            opt.option_id === optionId
                                ? { ...opt, count: opt.count + countChange }
                                : opt
                        ),
                    };
                }
                return cartItem;
            });
            setMainCart(updatedCart);
            return updatedCart;
        });
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.container}>
                <div
                    className={`${styles.items}`}
                    style={dialogCart.length === 0 ? { justifyContent: "center", alignItems: "center" } : {}}
                >
                    {dialogCart.length > 0 ? (
                        dialogCart.map((item) => {
                            const localizedTitle = (item[`name_${activeLanguage}` as keyof ICartItem] as string) || item.name_en;
                            return (
                                <div className={styles.item} key={item.id}>
                                    <div className={styles.itemHeader}>
                                        <div className={styles.titleBox}>
                                            <img src={`${baseURL}${item.image}`} className={styles.image} alt='image.png' />
                                            <p className={styles.title}>{localizedTitle}</p>
                                        </div>
                                        <div className={styles.deleteIcon} onClick={() => removeFromDialogCart(item.id)}>
                                            <img src={SmallTrash} alt='trash.svg' />
                                        </div>
                                    </div>
                                    <div className={styles.options}>
                                        {item.option.map((opt) => (
                                            <div className={styles.option} key={opt.option_id}>
                                                <p className={styles.portion}>{renderOptions(opt)}</p>
                                                <div className={styles.price}>
                                                    <p>{parseInt(opt.price)}</p>
                                                    <p>{currencySymbol}</p>
                                                </div>
                                                <ItemCounter
                                                    count={opt.count}
                                                    option={opt}
                                                    type={ItemCounterTypes.MAIN_CART}
                                                    handleAddOption={(_, c) =>
                                                        handleOptionCountChange(item.id, opt.option_id, c)
                                                    }
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        showEmptyCart && (
                            <div className={styles.emptyCart}>
                                <div className={styles.imageBox}>
                                    <img src={EmptyCart} alt='emptyCart.png' />
                                </div>
                                <p>{t('Visitor.Empty_Cart')}</p>
                            </div>
                        )
                    )}
                </div>
            </div>

            {dialogCart.length > 0 && isAnyActiveService ? (
                <div className={styles.buttonFooter}>
                    <div className={styles.noServiceButton}>
                        <div className={styles.iconRow}>
                            <PlateIcon className={styles.plateIcon} />
                            <p>{t('Visitor.Dine_In')}</p>
                        </div>
                        {serviceFee! > 0 && (
                            <div className={styles.serviceFee}>
                                {`${t('Visitor.Service_Fee')} ${formatPercent(String(serviceFee))}%`}
                            </div>
                        )}
                        <div className={styles.total}>
                            <p>{t('Visitor.Total')}</p>
                            <span>{`${parseInt(String(dineInPrice))} ${currencySymbol}`}</span>
                        </div>
                    </div>
                    <div className={styles.serviceButtons}>
                        {services?.delivery?.is_active && (
                            <div className={styles.deliveryButton} onClick={onOpenOrderDeliveryDialog}>
                                <DeliveryIconComponent className={styles.buttonIcon} />
                                <p>{t('Visitor.Delivery_Button')}</p>
                            </div>
                        )}
                        {services?.pre_order && (
                            <div className={styles.preOrderButton} onClick={onOpenPreOrderDialog}>
                                <PackageIcon className={styles.buttonIcon} color="white" />
                                <p>{t('Visitor.Pre_Order_Button')}</p>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className={styles.bottom}>
                    <div className={styles.totalRow}>
                      <div className={styles.iconRow}>
                            <PlateIcon className={styles.plateIcon} />
                            <p>{t('Visitor.Dine_In')}</p>
                        </div>
                        {serviceFee! > 0 && (
                            <div className={styles.serviceFee}>
                                {`${t('Visitor.Service_Fee')} ${formatPercent(String(serviceFee))}%`}
                            </div>
                        )}
                        <div className={styles.total}>
                            <p>{t('Visitor.Total')}</p>
                            <span>{`${parseInt(String(dineInPrice))} ${currencySymbol}`}</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MainCartDialog;
